import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer.js';
import ClassBreaksRenderer from '@arcgis/core/renderers/ClassBreaksRenderer.js';

import { useLayerRegistry } from '@/map/layers/registry';
import type { LayerLegend, LegendEntry } from '@/map/layers/types';
import { ensureFeatureLayer, requireLayerRecord } from './shared/layers';
import {
  parseColor,
  rgbToHex,
  symbolFor,
} from './shared/classification-symbols';
import type { Tool } from './types';

type Input = {
  layer_id: string;
  /** Identify which class to repaint. Provide ONE of value, label, or index. */
  value?: string | number;
  label?: string;
  index?: number;
  /** Hex (`#ff0000`, `ff0000`, `f00`) or a common color name (`red`, `teal`, …). */
  color: string;
};

export const layerSetClassColor: Tool<Input> = {
  name: 'layer_set_class_color',
  description:
    'Repaint a single class within a layer that was previously classified by `layer_classify` (unique-values or class-breaks). Identify the class by `value` (the underlying attribute value, e.g. "Residential"), `label` (the legend label as shown), or zero-based `index`. Color accepts hex (`#ff0000`) or common names (red, teal, …). Use this for "paint all residential buildings red" — call `layer_classify` first if the layer has not been classified yet.',
  input_schema: {
    type: 'object',
    properties: {
      layer_id: { type: 'string' },
      value: { type: ['string', 'number'] },
      label: { type: 'string' },
      index: { type: 'integer', minimum: 0 },
      color: {
        type: 'string',
        description: 'Hex color (e.g. "#ff0000") or a common name (red, teal, blue, …).',
      },
    },
    required: ['layer_id', 'color'],
    additionalProperties: false,
  },
  execute: async ({ layer_id, value, label, index, color }) => {
    const layer = await ensureFeatureLayer(layer_id);
    const record = requireLayerRecord(layer_id);

    const legend = record.legend;
    if (!legend) {
      throw new Error(
        `layer_set_class_color: layer "${layer_id}" is not classified — call layer_classify first.`,
      );
    }

    const rgb = parseColor(color);
    if (!rgb) {
      throw new Error(
        `layer_set_class_color: could not parse color "${color}". Use a hex code like "#ff0000" or a common name (red, teal, blue, …).`,
      );
    }

    const entry = pickEntry(legend, { value, label, index });
    if (!entry) {
      throw new Error(
        `layer_set_class_color: could not find a class on "${layer_id}" matching value=${
          value ?? 'n/a'
        }, label=${label ?? 'n/a'}, index=${index ?? 'n/a'}. Available: ${legend.entries
          .map((e) => `"${e.label}"`)
          .join(', ')}.`,
      );
    }

    const renderer = layer.renderer as __esri.Renderer | null | undefined;
    const geometryType =
      (layer.geometryType as string | undefined) ?? record.probe?.service.geometry_type;
    const newSymbol = symbolFor(geometryType, rgb);
    if (!newSymbol) {
      throw new Error(
        `layer_set_class_color: unsupported geometry type "${String(geometryType)}".`,
      );
    }

    if (legend.scheme === 'unique-values' && renderer instanceof UniqueValueRenderer) {
      const infos = renderer.uniqueValueInfos ?? [];
      const target = infos[entry.index];
      if (!target) throw new Error(`layer_set_class_color: class index ${entry.index} out of range.`);
      target.symbol = newSymbol as unknown as typeof target.symbol;
      // Reassign so ArcGIS sees the change and triggers a redraw.
      renderer.uniqueValueInfos = [...infos];
      layer.renderer = renderer.clone();
    } else if (legend.scheme === 'class-breaks' && renderer instanceof ClassBreaksRenderer) {
      const infos = renderer.classBreakInfos ?? [];
      const target = infos[entry.index];
      if (!target) throw new Error(`layer_set_class_color: class index ${entry.index} out of range.`);
      target.symbol = newSymbol as unknown as typeof target.symbol;
      renderer.classBreakInfos = [...infos];
      layer.renderer = renderer.clone();
    } else {
      throw new Error(
        `layer_set_class_color: layer "${layer_id}" renderer no longer matches its classification — re-run layer_classify.`,
      );
    }

    const newHex = rgbToHex(rgb);
    const updatedEntries: LegendEntry[] = legend.entries.map((e, i) =>
      i === entry.index ? { ...e, color: newHex } : e,
    );
    useLayerRegistry.getState().patch(layer_id, {
      legend: { ...legend, entries: updatedEntries },
    });

    return {
      ok: true,
      layer_id,
      class_index: entry.index,
      class_label: entry.label,
      color: newHex,
    };
  },
};

function pickEntry(
  legend: LayerLegend,
  selectors: { value?: string | number; label?: string; index?: number },
): LegendEntry | undefined {
  const { value, label, index } = selectors;
  if (typeof index === 'number') {
    return legend.entries[index];
  }
  if (value !== undefined && value !== null) {
    const v = String(value);
    const direct = legend.entries.find((e) => e.value === v);
    if (direct) return direct;
    const ci = legend.entries.find(
      (e) => e.value !== undefined && e.value.toLowerCase() === v.toLowerCase(),
    );
    if (ci) return ci;
  }
  if (label) {
    const direct = legend.entries.find((e) => e.label === label);
    if (direct) return direct;
    const ci = legend.entries.find((e) => e.label.toLowerCase() === label.toLowerCase());
    if (ci) return ci;
  }
  return undefined;
}
